import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OrderTrackingModal from "../../components/modals/orderTrackingModal";
import { Table, CustomButton} from "../../components/shared";
import {
    OrderRow,
    OrderStatusApiItem,
    OrderTrackingApiItem,
    OrderTrackingSearchKey,
    SEARCH_TYPES,
} from "./orderTracking-Types";
import { AppDispatch, RootState } from "../../redux/store";
import {
    resetPagination,
    setPage,
    setTotalItems,
    setTotalPages,
} from "../../redux/reducer/pagination/paginationSlice";

import { getApi } from "../../apis/api";
import {
    ORDER_TRACKING_DETAILS,
    ORDER_TRACKING_SEARCH,
} from "../../apis/end-points";
import { formatDate } from "../../utils/utils";
import moment from "moment";
import { useMessage } from "../../context/MessageContext";
import { Loader } from "../../components/loaders/loaders";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";

const mapApiResponseToRows = (items: OrderTrackingApiItem[]): OrderRow[] =>
    items.map((item) => ({
        id: item.id,
        viewStatus: "View Status",
        orderDate: item.orderDate ? formatDate(item.orderDate) : "--",
        orderNumber: item.orderNumber || "--",
        orderType: item.orderType || "--",
        orderStatus: item.orderStatus || "--",
        proNumber: item.proNumber || "--",
        reference1: item.reference1 || "--",
        reference2: item.reference2 || "--",
        bolNumber: item.bolNumber || "--",
    }));

const mapStatusResponse = (items: OrderStatusApiItem[], orderTypeName?: string) =>
    items.map((item) => ({
        status: orderTypeName === "LTL + Final Mile"
            ? item.orderStatusType?.name === "Booked"
                ? "Booked"
                : `${item.orderType ? item.orderType : "Final Mile"} - ${item.orderStatusType?.name ?? ""}`.trim()
            : item.orderType
                ? `${item.orderType} - ${item.orderStatusType?.name ?? ""}`.trim()
                : item.orderStatusType?.name ?? "-",
        location: item.statusLocation?.trim() || "-",
        zip: item.zipCode?.trim() || "-",
        date: (item.originalStatusDate || item.statusDate) ? moment.utc(item.originalStatusDate || item.statusDate).local().format("MM/DD/YYYY") : "-",
        time: (item.statusFromTime && item.statusToTime)
            ? `${moment.utc(item.statusFromTime).local().format("hh:mm A")} - ${moment.utc(item.statusToTime).local().format("hh:mm A")}`
            : (item.originalStatusDate || item.statusDate) ? moment.utc(item.originalStatusDate || item.statusDate).local().format("hh:mm A") : "-",
    }));

const OrderTracking: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { setMessage } = useMessage();
    const { currentPage, limit } = useSelector(
        (state: RootState) => state.pagination
    );
    const pageSize = limit ?? 100;

    const [searchType, setSearchType] = useState<OrderTrackingSearchKey>("all");
    const [searchValue, setSearchValue] = useState<string>("");
    const [activeSearch, setActiveSearch] = useState<{
        key: OrderTrackingSearchKey;
        value: string;
    } | null>({ key: "all", value: "" });
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<OrderRow | null>(null);
    const [trackingDetails, setTrackingDetails] = useState<
        ReturnType<typeof mapStatusResponse>
    >([]);
    const [loading, setLoading] = useState<boolean>(false);

    const handleViewStatus = async (row: OrderRow) => {
        setLoading(true);
        try {
            const response = await getApi<OrderStatusApiItem[]>(
                `${ORDER_TRACKING_DETAILS}${row.id}`
            );
            if (response?.success) {
                setTrackingDetails(mapStatusResponse(response.data ?? [], row.orderType));
                setSelectedOrder(row);
            } else {
                setMessage(
                    response.error?.message ??
                        GENERAL_ERROR_MESSAGE,
                    "error"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const closeTrackingModal = () => {
        setSelectedOrder(null);
        setTrackingDetails([]);
    };

    useEffect(() => {
        return () => {
            dispatch(resetPagination());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!activeSearch) return;
        let cancelled = false;

        const run = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    searchKey: activeSearch.key,
                    searchValue: activeSearch.value,
                    page: String(currentPage),
                    limit: String(pageSize),
                });
                const response = await getApi<OrderTrackingApiItem[]>(
                    `${ORDER_TRACKING_SEARCH}?${params.toString()}`
                );
                if (cancelled) return;

                if (response?.success) {
                    setOrders(mapApiResponseToRows(response.data ?? []));
                    const totalItems = response.pagination?.totalItems ?? 0;
                    const totalPages = response.pagination?.totalPages ?? 1;
                    const serverPage =
                        response.pagination?.currentPage ?? currentPage;
                    const clampedPage = Math.min(serverPage, totalPages);

                    dispatch(setTotalItems(totalItems));
                    dispatch(setTotalPages(totalPages));
                    if (clampedPage !== currentPage) {
                        dispatch(setPage(clampedPage));
                    }
                } else {
                    setOrders([]);
                    dispatch(setTotalItems(0));
                    dispatch(setTotalPages(1));
                    setMessage(
                        response.error?.message ?? GENERAL_ERROR_MESSAGE,
                        "error"
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [activeSearch, currentPage, pageSize, dispatch]);

    const columns = [
        {
            key: "viewStatus",
            header: "View Status",
            isSortable: false,
            onCellClick: (row: OrderRow) => handleViewStatus(row),
        },
        { key: "orderDate", header: "Order Date", isSortable: false },
        { key: "orderNumber", header: "Order Number", isSortable: false },
        { key: "orderType", header: "Order Type", isSortable: false },
        { key: "orderStatus", header: "Order Status", isSortable: false },
        { key: "proNumber", header: "Pro#", isSortable: false },
        { key: "bolNumber", header: "BOL#", isSortable: false },
    ];

    const handleTrack = () => {
        const trimmed = searchValue.trim();
        if (!trimmed) {
            return;
        }
        dispatch(setPage(1));
        setActiveSearch({ key: searchType, value: trimmed });
    };

    return (
        <section className="oderTracking">
            <div className="oderTracking__container">
                <div className="oderTracking__content">
                    <div className="oderTracking__head">
                        <h1 className="title">Order Tracking</h1>
                        <div className="oderTracking__head__info">
                            <div className="formRow">
                                <div className="form__radio form__radio--gray">
                                    {SEARCH_TYPES.map((type) => (
                                        <div className="form__radio__option" key={type.id}>
                                            <input
                                                type="radio"
                                                id={type.id}
                                                name="searchType"
                                                className="form__radio__input"
                                                checked={searchType === type.id}
                                                onChange={() => setSearchType(type.id)}
                                            />
                                            <label htmlFor={type.id} className="form__radio__label">
                                                {type.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="searchBoxWrap">
                                <div className="form__Field">
                                    <input
                                        type="text"
                                        className="form__input form__input--sm"
                                        placeholder="Type Here"
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleTrack();
                                        }}
                                    />
                                </div>
                                <div className="btn__group">
                                    <CustomButton
                                        buttonText="Track"
                                        customClassName="btn__primary"
                                        handleButtonClick={handleTrack}
                                        disabled={loading || !searchValue.trim()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="oderTracking__body">
                        <div className="tableWrap">
                            <Table
                                columns={columns}
                                data={orders}
                                setLoading={setLoading}
                                table="OrderTrackingTable"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {selectedOrder && (
                <OrderTrackingModal
                    isOpen={true}
                    onClose={closeTrackingModal}
                    orderNumber={selectedOrder.orderNumber}
                    data={trackingDetails}
                />
            )}

            {loading && <Loader />}
        </section>
    );
};

export default OrderTracking;
